import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Button } from 'antd'
import { API, Auth } from 'aws-amplify'
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import ResultModal from '../../../components/ResultModal.js'
import i18n from '../../../i18n/index.js'
import { apiPost } from '../../../libs/apiHelper.js'
import { useAppContext } from '../../../libs/context.js'
import {
	parseSupplierToTemplateRbm,
	parseTemplateToSupplierRbm
} from '../../../libs/parse.js'
import {
	CARD_TYPE,
	CAROUSEL_TYPE,
	RBM_CONVERSATIONAL_TYPE,
	RBM_ELEMENT_ERRORS,
	RBM_SIMPLE_TYPE,
	RBM_TYPES,
	REPLY_BUTTON
} from './components/constants.js'
import CreateRbm from './components/CreateRbm.jsx'
import InteractiveDemo from './components/InteractiveDemo.jsx'
import NewSmsForm from './components/NewSmsForm.js'
import PhonePreview from './components/PhonePreview.jsx'
import LanguageSelector from '../../../components/LanguageSelector.jsx'
import {
	applyI18nToElements,
	availableLangsForTemplate,
	parseTemplateI18n
} from '../../../libs/templateI18n.js'
import './phone.css'

const handleCreateButton = () => {
  //TODO para crear el objeto del flow final unir todos los mensaje con Object.assign({}, ...messagesList)
}

const AlertPanel = () => {
  return (
    <div className="rounded-md bg-yellow-50 p-4 m-10">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon
            className="h-5 w-5 text-yellow-400"
            aria-hidden="true"
          />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">Atention</h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>{i18n.t('rbm_alert')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

const modalMsg = {
  ok: {
    title: i18n.t('operation_performed'),
    content: i18n.t('created_campaign'),
    cta: 'Ok'
  },
  error: {
    title: 'Error',
    content: i18n.t('error_creating_campaign'),
    cta: 'Error'
  }
}

const getImagesFromTemplate = (template) => {
  //the template is the rbmElements object
  const images = {}
  Object.keys(template).forEach((key) => {
    if (template[key].type === CARD_TYPE && !template[key].image.uploaded) {
      images[template[key].image.hash] = template[key].image.file
    }
    if (template[key].type === CAROUSEL_TYPE) {
      template[key].cards.forEach((card) => {
        if (!card.image.uploaded) {
          images[card.image.hash] = card.image.file
        }
      })
    }
  })
  return images
}

const uploadImgaes = async (images) => {
  //convert image to sent it in the aws api post
  const convertImageToFormData = (image) => {
    const formData = new FormData()
    formData.append('file', image)
    return formData
  }

  let promises = []
  for (let key in images) {
    const file = images[key]
    const formData = convertImageToFormData(file)
    formData.append('key', key)
    const params = {
      key: key,
      file: formData
    }
    promises.push(
      apiPost('/upload-image', {
        body: formData
      })
    )
  }
  let result = []
  try {
    result = await Promise.all(promises)
  } catch (err) {
    console.log(err)
  }
  const imagesUploaded = result.reduce((acc, res) => {
    acc[res.key] = res.location
    return acc
  }, {})
  return imagesUploaded
}

const buildRbmFlows = (rbmElements) => {
  const copy = { ...rbmElements }
  delete copy.rbmFlowAliasesMap
  const grouped = {}
  Object.keys(copy)
    .map((key) => key.split('-')[0])
    .forEach((flow) => {
      if (!grouped[flow]) {
        grouped[flow] = []
        const flowLength = Object.keys(copy).filter((k) => k.split('-')[0] === flow).length
        for (let i = 0; i < flowLength; i++) {
          grouped[flow].push(copy[`${flow}-${i + 1}/${flowLength}`])
        }
      }
    })
  return grouped
}

const getRbmElements = (elements, supplier, campaignName) => {
  const rbmElements = {}
  //parse from supplier to template
  //remove the campaign name from the keys
  for (let key in elements) {
    //check if starts with the campaign name and doesnt have 'initSMS' in the key
    if (key.startsWith(campaignName) && !key.includes('initSMS')) {
      const splitKey = key.split('-')
      const newKey = splitKey.slice(1).join('-')
      rbmElements[newKey] = elements[key]
    }
  }
  const parsedElements = parseSupplierToTemplateRbm(rbmElements, supplier)
  if (elements.rbmFlowAliasesMap) {
    parsedElements.rbmFlowAliasesMap = elements.rbmFlowAliasesMap
  }
  return parsedElements
}

const CreateCampaign = ({ template, setTemplates, setSelectedTemplate }) => {
  const defaultErrors = {
    invalidName: false,
    emptyName: template ? false : true,
    emptySms: template ? false : true,
    invalidVariable: false,
    invalidSender: false,
    emptySender: template && template.sender ? false : true
  }
  const { userInfo } = useAppContext()

  const errorText = {
    invalidName: i18n.t('error_space_name'),
    emptyName: i18n.t('error_empty_name'),
    emptySms: i18n.t('error_empty_sms'),
    invalidVariable: i18n.t('error_invalid_variable'),
    invalidSender: i18n.t('error_invalid_sender'),
    emptySender: i18n.t('error_empty_sender')
  }

  const [rbmElements, setRbmElements] = useState({})
  const [openCreate, setOpenCreate] = useState(false)
  const [isSms, setIsSms] = useState(true)
  const [rbmType, setRbmType] = useState(
    template ? template.rbmType || null : null
  )
  const [mobileMessages, setMobileMessages] = useState([])
  const [smsList, setSmsList] = useState('')
  const [campaignName, setCampaignName] = useState(
    template ? template.name : ''
  )
  const [sender, setSender] = useState(
    template && template.sender ? template.sender : ''
  )
  const [modalContent, setModalContent] = useState(modalMsg.ok)
  const [openResult, setOpenResult] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [errors, setErrors] = useState(defaultErrors)
  const [templateSMS, setTemplateSMS] = useState(
    template 
      ? (typeof template.elements === 'string' 
          ? JSON.parse(template.elements) 
          : template.elements)[`${template.name}-initSMS`] 
      : null
  )
  const [rbmSimpleErrors, setRbmSimpleErrors] = useState([])
  const [supplierRcs, setSupplierRcs] = useState('orange')
  const [flowConfig, setFlowConfig] = useState({})
  const [selectedLang, setSelectedLang] = useState('es')

  // Parse i18n once from the template row (stored as JSON string in DDB).
  const templateI18n = useMemo(
    () => parseTemplateI18n(template && template.i18n),
    [template]
  )
  const availableLangs = useMemo(
    () => availableLangsForTemplate(templateI18n),
    [templateI18n]
  )

  const rbmFlows = useMemo(() => buildRbmFlows(rbmElements), [rbmElements])
  const rbmFlowAliases = useMemo(() => rbmElements.rbmFlowAliasesMap || {}, [rbmElements])

  // Objeto template tal como está guardado en DynamoDB (para el JSON viewer)
  const fullTemplate = useMemo(() => {
    if (!template) return null
    const result = { ...template }
    if (typeof result.elements === 'string') {
      try {
        result.elements = JSON.parse(result.elements)
      } catch (e) {
        console.error('Error parsing template.elements:', e)
      }
    }
    if (typeof result.emailFields === 'string') {
      try {
        result.emailFields = JSON.parse(result.emailFields)
      } catch (e) {
        console.error('Error parsing template.emailFields:', e)
      }
    }
    if (typeof result.expectedFlow === 'string') {
      try {
        result.expectedFlow = JSON.parse(result.expectedFlow)
      } catch (e) {
        console.error('Error parsing template.expectedFlow:', e)
      }
    }
    if (typeof result.formFlow === 'string') {
      try {
        result.formFlow = JSON.parse(result.formFlow)
      } catch (e) {
        console.error('Error parsing template.formFlow:', e)
      }
    }
    return result
  }, [template])

  const getUserInfo = async () => {
    const user = await Auth.currentAuthenticatedUser()
    const userFullInfo = await API.get('rcs-api', '/user', {
      headers: {
        Authorization: user.signInUserSession.idToken.jwtToken
      }
    })
    return userFullInfo
  }

  const getInitialRbmElements = async () => {
    if (!template) return
    const userFullInfo = await getUserInfo()
    const userSupplier = userFullInfo.supplierRcs || 'orange'
    // Manejar elements como string JSON o como objeto ya parseado
    const rawElements = typeof template.elements === 'string'
      ? JSON.parse(template.elements)
      : template.elements
    // i18n overlay for preview: swaps textMessage + displayText (no routing).
    const elements = applyI18nToElements(rawElements, selectedLang, templateI18n)
    const newRbmElements = getRbmElements(elements, userSupplier, campaignName)
    setSupplierRcs(userSupplier)
    setRbmElements(newRbmElements)
  }

  useEffect(() => {
    getInitialRbmElements()
    // Initialize isSms based on template type when editing
    if (template && template.rbmType) {
      setIsSms(false)
    }
    // Also re-derive when the selected preview language changes so downstream
    // rbmFlows/PhonePreview/InteractiveDemo pick up the translated copy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLang])

  const history = useHistory()

  function goToList() {
    history.push('/templates')
    if (setSelectedTemplate) {
      setSelectedTemplate(null)
    }
  }

  const existErrors = () => {
    for (let error in errors) {
      if (errors[error]) {
        return true
      }
    }
    return false
  }
  const existErrorsResult = useMemo(() => existErrors())

  const addUrlToImages = (elements, imagesUploaded) => {
    const newElements = { ...elements }
    Object.keys(elements).forEach((key) => {
      const element = elements[key]
      if (element.type === CARD_TYPE) {
        if (element.image.uploaded) return
        newElements[key].image = {
          ...element.image,
          url: imagesUploaded[element.image.hash] || ''
        }
        delete newElements[key].image.file
      }
      if (element.type === CAROUSEL_TYPE) {
        const newCards = element.cards.map((card) => {
          let newCard = { ...card }
          // Solo procesar tarjetas con imagen no subida
          if (card.image && !card.image.uploaded) {
            delete newCard.image.file
            newCard.image.url = imagesUploaded[newCard.image.hash] || ''
          }
          return newCard
        })
        newElements[key].cards = newCards
      }
    })
    return newElements
  }

  const addPostBackToButton = (button, elements, campaignName) => {
    const newButton = { ...button }
    if (button.replyFlow) {
      const replyFlow = button.replyFlow
      const flowElementsCount = Object.keys(elements).filter((key) =>
        key.startsWith(`${campaignName}-${replyFlow}-`)
      ).length
      if (flowElementsCount !== 0) {
        // The destination element does NOT include -1/1 suffix in the postback.
        // The parser in filterRcs.js strips metadata (parent, addFlow, etc.) and
        // the remaining -1/1 suffix becomes part of the element name.
        // Example: "caixarecobros-nameCheck-parent:[...]-addFlow:[...]-1/1"
        //   -> after stripping parent/addFlow -> "caixarecobros-nameCheck-1/1"
        let postback = `${campaignName}-${replyFlow}`
        
        // Inject addFlow if present
        if (button.addFlow) {
          postback += `-addFlow:[${button.addFlow}]`
        }
        
        // Inject saveText if present
        if (button.saveText) {
          postback += `-saveText:[${button.saveText}]`
        }
        
        // Inject parent if present
        if (button.parent) {
          postback += `-parent:[${button.parent}]`
        }
        
        // Inject action if present
        if (button.action === 'send-email' && button.sendEmail) {
          const emailIndex = button.sendEmailIndex || '1'
          if (button.customEmail) {
            postback += `-action:[send-email-${emailIndex}:${button.customEmail}]`
          } else {
            postback += `-action:[send-email-${emailIndex}]`
          }
        }
        
        // Inject schedule if present
        if (button.schedule) {
          postback += `-schedule:[${button.schedule}]`
        }
        
        // Append the index/total suffix (this becomes the -1/1 part of the element key)
        postback += `-1/1`
        newButton.postback = postback
      }
    }
    return newButton
  }

  const addPostbackToCardButtons = (card, elements, campaignName) => {
    const newCard = { ...card }
    if (card.buttons) {
      const newButtons = card.buttons.map((button) => {
        if (button.type === REPLY_BUTTON) {
          return addPostBackToButton(button, elements, campaignName)
        }
        return button
      })
      newCard.buttons = newButtons
    }
    return newCard
  }

  const addPostBackToButtons = (elements, campaignName) => {
    const newElements = { ...elements }
    for (let key in elements) {
      if (key === 'rbmFlowAliasesMap') continue // Skip non-element keys
      const element = elements[key]
      if (!element) continue // Skip undefined/null elements
      if (element.type === CARD_TYPE) {
        const newCard = addPostbackToCardButtons(
          element,
          elements,
          campaignName
        )
        newElements[key] = newCard
      }
      if (element.type === CAROUSEL_TYPE) {
        const newCards = element.cards.map((card) =>
          addPostbackToCardButtons(card, elements, campaignName)
        )
        newElements[key].cards = newCards
      }
      if (element.suggestions) {
        const newSuggestions = element.suggestions.map((suggestion) => {
          if (suggestion.type === REPLY_BUTTON) {
            return addPostBackToButton(suggestion, elements, campaignName)
          }
          return suggestion
        })
        newElements[key].suggestions = newSuggestions
      }
    }
    return newElements
  }

  const create = async () => {
    setIsLoading(true)
    setOpenResult(true)

    if (rbmType === RBM_SIMPLE_TYPE && rbmElements['init-1/1'].text === '') {
      setRbmSimpleErrors([RBM_ELEMENT_ERRORS.EMPTY_TEXT])
      setIsSms(false)
      return
    } else {
      setRbmSimpleErrors([])
    }
    // add the campaign name to the begining of the rbm elements keys
    const rbmElementsKeys = Object.keys(rbmElements)
    let newRbmElements = {}
    rbmElementsKeys.forEach((key) => {
      if (key === 'rbmFlowAliasesMap') {
        newRbmElements[key] = rbmElements[key]
      } else {
        newRbmElements[`${campaignName}-${key}`] = rbmElements[key]
      }
    })

    const images = getImagesFromTemplate(newRbmElements)
    const imagesUploaded = await uploadImgaes(images)
    newRbmElements = addUrlToImages(newRbmElements, imagesUploaded)

    let newflow = {
      type: 'direct'
    }
    newRbmElements = addPostBackToButtons(newRbmElements, campaignName)

    console.log('newRbmElements', newRbmElements)

    const parsedElements = parseTemplateToSupplierRbm(
      newRbmElements,
      supplierRcs
    )
    newflow = {
      ...parsedElements,
      ...newflow,
      [campaignName + '-initSMS']: smsList
    }

    try {
      const body = {
        name: campaignName,
        elements: JSON.stringify(newflow),
        sender: sender,
        rbmType: rbmType
      }
      if (flowConfig.formFlow !== undefined) {
        body.formFlow = flowConfig.formFlow
      } else if (fullTemplate?.formFlow) {
        body.formFlow = fullTemplate.formFlow
      }
      if (flowConfig.expectedFlow !== undefined) {
        body.expectedFlow = flowConfig.expectedFlow
      } else if (fullTemplate?.expectedFlow) {
        body.expectedFlow = fullTemplate.expectedFlow
      }
      if (fullTemplate?.emailFields) {
        body.emailFields = JSON.stringify(fullTemplate.emailFields)
      }
      if (template) {
        body.template = template
      }
      const res = await apiPost('/campaign-type', { body })
      if (template) {
        setTemplates((oldTemplates) =>
          oldTemplates.map((t) => {
            let newTemplate = t
            if (t.name === campaignName) {
              newTemplate.text =
                smsList?.length > 20 ? `${smsList.substr(0, 50)}...` : smsList
              newTemplate.sender = sender
              newTemplate.elements = JSON.stringify(newflow)
              if (body.formFlow !== undefined) newTemplate.formFlow = JSON.stringify(body.formFlow)
              if (body.expectedFlow !== undefined) newTemplate.expectedFlow = JSON.stringify(body.expectedFlow)
              if (body.emailFields !== undefined) newTemplate.emailFields = body.emailFields
              return newTemplate
            }
            return newTemplate
          })
        )
      }
      setModalContent(modalMsg.ok)
    } catch (err) {
      console.log(err)
      setModalContent(modalMsg.error)
    }
    setIsLoading(false)
  }

  const handleJsonSave = async (editedData) => {
    try {
      const body = {
        name: editedData.name || campaignName,
        elements: JSON.stringify(editedData.elements),
        sender: editedData.sender || sender,
        rbmType: editedData.rbmType || rbmType,
      }
      if (editedData.emailFields) {
        body.emailFields = JSON.stringify(editedData.emailFields)
      }
      if (editedData.formFlow) {
        body.formFlow = editedData.formFlow
      }
      if (editedData.expectedFlow) {
        body.expectedFlow = editedData.expectedFlow
      }
      if (template) body.template = template
      await apiPost('/campaign-type', { body })
      setTemplates((prev) =>
        prev.map((t) =>
          t.name === campaignName
            ? {
                ...t,
                elements: JSON.stringify(editedData.elements),
                sender: body.sender,
                ...(body.formFlow && { formFlow: body.formFlow }),
                ...(body.expectedFlow && { expectedFlow: body.expectedFlow }),
                ...(body.emailFields && { emailFields: body.emailFields })
              }
            : t
        )
      )
    } catch (err) {
      console.error('handleJsonSave error:', err)
      throw err
    }
  }

  return (
    <div className="pb-20 -mt-4">
      <ResultModal
        {...modalContent}
        isLoading={isLoading}
        open={openResult}
        setOpen={setOpenResult}
        method={goToList}
      />

      {/* Floating global language selector — visible in every preview mode
          so admins editing conversational flows can also toggle the demo. */}
      {availableLangs.length > 1 && (
        <div className="fixed top-4 right-4 z-40 bg-white/95 backdrop-blur border border-gray-200 rounded-full shadow-md px-3 py-1.5">
          <LanguageSelector
            langs={availableLangs}
            value={selectedLang}
            onChange={setSelectedLang}
          />
        </div>
      )}

      {/* Contenido principal */}
      <div className="flex gap-6 justify-between">
        {isSms ? (
          <NewSmsForm
            setSender={setSender}
            sender={sender}
            setMobileMessages={setMobileMessages}
            setSmsList={setSmsList}
            smsList={smsList}
            setErrors={setErrors}
            errors={errors}
            errorText={errorText}
            templateSMS={templateSMS}
          />
        ) : userInfo.isAdmin ? (
          <CreateRbm
            setMobileMessages={setMobileMessages}
            rbmType={rbmType}
            setRbmType={setRbmType}
            setRbmElements={setRbmElements}
            rbmElements={rbmElements}
            rbmSimpleErrors={rbmSimpleErrors}
            emailFields={template?.emailFields ? JSON.parse(template.emailFields) : []}
            campaignName={campaignName}
            templateElements={template?.elements}
            fullTemplate={fullTemplate}
            onJsonSave={handleJsonSave}
            onFlowConfigChange={(data) => setFlowConfig(prev => ({
              formFlow: data.formFlow !== undefined ? data.formFlow : prev.formFlow,
              expectedFlow: data.expectedFlow !== undefined ? data.expectedFlow : prev.expectedFlow
            }))}
          />
        ) : rbmType === RBM_CONVERSATIONAL_TYPE ? (
          <div className="sticky top-0 self-start h-[calc(100vh-120px)] py-2 flex flex-col gap-2">
            <LanguageSelector
              langs={availableLangs}
              value={selectedLang}
              onChange={setSelectedLang}
              className="self-center"
            />
            <InteractiveDemo
              rbmFlows={rbmFlows}
              rbmFlowAliases={rbmFlowAliases}
            />
          </div>
        ) : (
          <AlertPanel />
        )}
        {/* PhonePreview - hidden for conversational type as it has its own integrated demo */}
        {rbmType !== RBM_CONVERSATIONAL_TYPE && (
          <div className="sticky h-fit top-10 z-10 flex flex-col gap-2 items-center">
            <LanguageSelector
              langs={availableLangs}
              value={selectedLang}
              onChange={setSelectedLang}
            />
            {(isSms || rbmType) && (
              <PhonePreview rbmElements={mobileMessages} />
            )}
          </div>
        )}
      </div>

      {/* Botón volver para usuarios no-admin */}
      {!userInfo.isAdmin && (
        <div className="fixed bottom-6 z-50 left-[calc(50%+128px)] transform -translate-x-1/2">
          <Link to={'/templates'}>
            <button
              type="button"
              onClick={() => { if (setSelectedTemplate) setSelectedTemplate(null) }}
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-gray-700 bg-white/95 border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 shadow-md"
            >
              ← {i18n.t('cancel')}
            </button>
          </Link>
        </div>
      )}

      {/* Floating Action Bar — solo para admins */}
      {userInfo.isAdmin && <div className="fixed bottom-6 z-50 left-[calc(50%+128px)] transform -translate-x-1/2">
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-white/90 backdrop-blur-2xl border-2 border-white/40 shadow-2xl ring-2 ring-white/30">
          {/* Campaign Name Input */}
          <div className="relative">
            <input
              type="text"
              name="name"
              id="name"
              disabled={template ? true : false}
              className={`w-48 text-sm font-semibold rounded-full px-4 py-2.5 transition-all ${
                errors.emptyName || errors.invalidName
                  ? 'bg-red-50 text-gray-900 border-2 border-red-300 focus:outline-none focus:border-red-500'
                  : template
                  ? 'text-gray-600 bg-gray-100/80 border-2 border-transparent cursor-not-allowed'
                  : 'text-gray-900 bg-white/95 border-2 border-gray-200 focus:outline-none focus:border-indigo-400 hover:border-gray-300'
              }`}
              placeholder={i18n.t('name')}
              value={campaignName}
              onChange={(e) => {
                setCampaignName(e.target.value)
                if (e.target.value === '') {
                  setErrors((oldErrors) => {
                    oldErrors.emptyName = true
                    return oldErrors
                  })
                } else {
                  setErrors((oldErrors) => {
                    oldErrors.emptyName = false
                    return oldErrors
                  })
                }
              }}
            />
            {(errors.emptyName || errors.invalidName) && (
              <p className="absolute -bottom-5 left-4 text-red-500 text-[10px] font-medium whitespace-nowrap">
                {errors.emptyName ? errorText.emptyName : errorText.invalidName}
              </p>
            )}
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-gray-300/50" />

          {/* SMS/RBM Toggle */}
          <div className="flex items-center bg-gray-100/80 rounded-full p-1">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                setIsSms(true)
                setMobileMessages([])
              }}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                isSms
                  ? 'bg-white text-indigo-600 shadow-md'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              SMS
            </button>
            {template && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  setIsSms(false)
                  setMobileMessages([])
                }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                  !isSms
                    ? 'bg-white text-indigo-600 shadow-md'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                RBM
              </button>
            )}
          </div>

          {/* RBM Type Selector - shown when RBM is selected */}
          {!isSms && rbmType && (
            <>
              <div className="w-px h-8 bg-gray-300/50" />
              <button
                type="button"
                onClick={() => {
                  setRbmType(null)
                  setMobileMessages([])
                }}
                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 rounded-full border border-indigo-200 hover:border-indigo-300 transition-all duration-300 group"
                title="Click to change RBM type"
              >
                <div className="w-2 h-2 rounded-full bg-indigo-500" />
                <span className="text-xs font-semibold text-indigo-700">
                  {RBM_TYPES.find(t => t.type === rbmType)?.name || 'RBM'}
                </span>
                <svg className="w-3 h-3 text-indigo-400 group-hover:text-indigo-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </>
          )}

          {/* Divider */}
          <div className="w-px h-8 bg-gray-300/50" />

          {/* Cancel Button */}
          <Link to={'/templates'}>
            <button
              type="button"
              onClick={() => {
                if (template && setSelectedTemplate) {
                  setSelectedTemplate(null)
                }
              }}
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-gray-700 bg-white/95 border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 hover:scale-105 active:scale-95 shadow-sm"
            >
              {i18n.t('cancel')}
            </button>
          </Link>

          {/* Save/Update Button */}
          <button
            type="button"
            disabled={existErrorsResult}
            onClick={(e) => {
              e.preventDefault()
              create()
            }}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all duration-300 shadow-md ${
              !existErrorsResult
                ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 hover:scale-105 active:scale-95 hover:shadow-lg'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {template ? i18n.t('update_template') : i18n.t('create_template')}
          </button>
        </div>
      </div>}
    </div>
  )
}

export default CreateCampaign
